// Draft of an IN-PROGRESS booking (chosen service and start + the details already typed), preserved
// across a Google sign-in round trip on a TENANT host.
//
// WHY THIS EXISTS (BUG-195). Google Identity Services only trusts the platform origin
// (see lib/authBridge.js's module docstring), so a tenant-host sign-in cannot run in
// place: `beginBridgeSignin` does a full `window.location.assign` to the platform origin
// and back, which UNMOUNTS Reserver.jsx entirely. Everything useRequireAuth.js restores on
// that return (docs/auth-and-admin/client-otp-auth-contract.md) is the SESSION — nothing
// restored the booking page's own component state, so the visitor came back signed in with
// an empty form and no time selected. Same idiom as lib/pendingBooking.js (written right before a
// Stripe redirect, read back on return) and lib/pendingConsult.js: a third sibling, written
// right before the Google bridge redirect instead of the Stripe one.
//
// TTL IS SHORT ON PURPOSE — 10 minutes, not lib/pendingBooking.js's 35 (that one is sized
// for a Stripe checkout; this one only has to survive a Google login).
//
// A FIRST VERSION OF THIS FILE GATED ONLY ON `tenantSlug` + the TTL, and READ
// UNCONDITIONALLY ON MOUNT — a fix-verifier refuted it: nothing checked that THIS page
// load was actually a return from the bridge, so a second visitor opening the same
// practice's booking page inside the 10-minute window, on the same browser, silently
// inherited the first visitor's name/phone/notes (and, because `form` was stored whole,
// their typed EMAIL too — the module claimed it never stored one; it did). Both defects
// share one root cause: nothing about the read proved the reader had just come from the
// bridge that wrote it.
//
// THE FIX IS THE NONCE, NOT A NEW FLAG. `beginBridgeSignin` already mints a single-use,
// tab-scoped nonce for anti-CSRF (sessionStorage, never shared across tabs even on the
// same origin — see lib/authBridge.js) and the server already requires it back at
// `/auth/handoff/finalize`. `writeBookingDraft` is called with that SAME nonce (threaded
// through `ClientAuthPanel` → `beginBridgeSignin`'s `onNonceReady` →
// `useRequireAuth`'s `onBeforeGoogleBridge`), and `readAndClearBookingDraft` requires the
// caller to hand back the nonce `finalizeBridgeIfPending` read out of sessionStorage for
// THIS mount (`useRequireAuth`'s `bridgeNonce`). A mount that never went through the
// bridge has no nonce to offer (`bridgeNonce` is `null`, never a guess), so it gets
// nothing, unconditionally — there is no code path left that reads this draft without
// first proving "I am the browser tab that just finished this exact handoff".
//
// `tenantSlug` is still checked too, as a second, independent axis (a stray draft must
// not survive a switch of practice even if a nonce were somehow reused).
//
// FIELDS ARE AN EXPLICIT ALLOWLIST, not a raw echo of the caller's form object — the
// email regression above happened because `writeBookingDraft` stored whatever `form` it
// was handed. `firstName`/`lastName`/`phone`/`notes` are what restores the visitor's
// PROGRESS (the row's own "entered details"); `email` is deliberately never accepted
// here, in code, not just in a comment — it is always the signed-in identity once
// restored (Reserver.jsx reads `user.email`), never a resumed typed value.
const KEY = "solunary_booking_draft";
const TTL_MS = 10 * 60 * 1000;
const FORM_FIELDS = ["firstName", "lastName", "phone", "notes"];

function pickForm(form) {
  const out = {};
  for (const f of FORM_FIELDS) {
    if (form && typeof form[f] === "string") out[f] = form[f];
  }
  return out;
}

export function writeBookingDraft({ tenantSlug, nonce, serviceId, start, form, promoCode, giftCardCode, marketingOptIn }) {
  if (!nonce) return; // no bridge attempt in flight: nothing to key a draft to
  try {
    localStorage.setItem(KEY, JSON.stringify({
      tenantSlug, nonce, serviceId, start, form: pickForm(form), promoCode, giftCardCode,
      // The client's own tick of the optional marketing box: only a literal `true` is
      // kept, so a restored page can never show a box ticked that the client did not tick.
      marketingOptIn: marketingOptIn === true,
      until: Date.now() + TTL_MS,
    }));
  } catch (e) { /* storage unavailable: the round trip proceeds, the form is just empty on return */ }
}

/**
 * @param {string} tenantSlug  the CURRENT page's practice.
 * @param {string|null|undefined} nonce  the nonce `useRequireAuth`'s `bridgeNonce`
 *   reports for THIS mount — `null` (not a bridge return) or `undefined` (bridge check
 *   still pending) both refuse the read, exactly like a missing one.
 */
export function readAndClearBookingDraft(tenantSlug, nonce) {
  try {
    const raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.until || Date.now() > d.until) return null;
    if (!nonce || d.nonce !== nonce) return null;
    if (d.tenantSlug !== tenantSlug) return null;
    return d;
  } catch (e) {
    return null;
  }
}
